import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiX, FiUploadCloud, FiClock, FiAlertTriangle, FiCheckCircle, FiInfo,
} from 'react-icons/fi';
import CountryFlag from './CountryFlag';

/** Minimal shape needed to render an order row inside the popup. Both
 *  PurchaseOrderList AND PurchaseOrderDetail are compatible with this. */
export interface SapSyncOrderRow {
  id: string;
  amazonPurchaseOrderNumber: string;
  status: number;            // Amazon-side status (PurchaseOrderStatus)
  channel: number;           // 0 = SellerCentral, 1 = Vendor
  marketplace: string | null; // e.g. "Amazon.de"
  shipToCountry?: string | null;
  totalAmount: number;
  currency: string;
}

interface ConfirmSapSyncDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /** The orders the user is about to queue. Single-element array is fine for
   *  the detail-page case; the dialog adapts its copy to "this order" wording. */
  orders: SapSyncOrderRow[];
  /** Resolves a numeric Amazon-side status into a localized label. Passed in to
   *  avoid the dialog importing the page-level status map. */
  statusLabel?: (status: number) => string;
}

/* ------------------------------------------------------------------ */
/*  Marketplace name -> ISO country (mirrors PurchaseOrdersPage)       */
/* ------------------------------------------------------------------ */

const marketplaceToCountry: Record<string, string> = {
  'amazon.de': 'DE', 'amazon.fr': 'FR', 'amazon.it': 'IT', 'amazon.es': 'ES',
  'amazon.co.uk': 'GB', 'amazon.com': 'US', 'amazon.pl': 'PL', 'amazon.se': 'SE',
  'amazon.nl': 'NL', 'amazon.com.be': 'BE', 'amazon.co.jp': 'JP', 'amazon.ca': 'CA',
  'amazon.com.au': 'AU', 'amazon.com.mx': 'MX', 'amazon.in': 'IN', 'amazon.com.br': 'BR',
  'amazon.com.tr': 'TR', 'amazon.ae': 'AE', 'amazon.sa': 'SA', 'amazon.sg': 'SG',
};
const getMarketplaceCountry = (mp: string | null | undefined): string | null => {
  if (!mp) return null;
  const k = mp.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return marketplaceToCountry[k] || null;
};

/**
 * Confirmation popup for "Zatwierdź przekazanie do SAP". Same visual language as
 * ReparseAllDialog (callout blocks + accent-colored confirm button + colored
 * info / warning sections), but tuned for the SAP-queue use case:
 *   - Lead paragraph
 *   - Blue callout: what this does (queue + ~15 min sync window)
 *   - Amber callout: "verify these orders are correct before clicking confirm"
 *   - Compact list of orders being queued, each row showing PO#, channel,
 *     marketplace, Amazon status, amount
 *
 * Dark/light theme works through CSS vars + RGBA accents (no hard-coded white/black).
 */
const ConfirmSapSyncDialog: React.FC<ConfirmSapSyncDialogProps> = ({
  isOpen, onConfirm, onCancel, loading = false, orders, statusLabel,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const single = orders.length === 1;
  const totalValue = orders.reduce((acc, o) => acc + (o.totalAmount || 0), 0);
  // If all orders share a currency we display the total summed; otherwise show "—"
  // because summing across currencies makes no sense.
  const sharedCurrency = orders.every(o => o.currency === orders[0]?.currency)
    ? (orders[0]?.currency ?? 'EUR')
    : null;

  const formatMoney = (amount: number, currency?: string | null) => {
    const cur = currency && currency.length === 3 ? currency : 'EUR';
    try {
      return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
    } catch {
      return `${amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
    }
  };

  const channelLabel = (ch: number): string =>
    ch === 1 ? t('orders.channelVendor') : t('orders.channelSellerCentral');

  return (
    <div
      className="modal-overlay"
      onClick={loading ? undefined : onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sap-sync-title"
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '720px' }}
      >
        {/* --- Header --------------------------------------------------- */}
        <div className="modal-header" style={{ alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(59, 130, 246, 0.12)',
              color: '#3b82f6',
              fontSize: '20px',
              flexShrink: 0,
            }}>
              <FiUploadCloud />
            </div>
            <div>
              <h2 id="sap-sync-title" style={{ margin: 0 }}>
                {t('orders.sapSyncDialogTitle')}
              </h2>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                {single
                  ? t('orders.sapSyncDialogSubtitleSingle')
                  : t('orders.sapSyncDialogSubtitleMany', { count: orders.length })}
              </div>
            </div>
          </div>
          <button
            className="btn btn-icon"
            onClick={onCancel}
            disabled={loading}
            aria-label={t('common.close')}
          >
            <FiX />
          </button>
        </div>

        {/* --- Body ----------------------------------------------------- */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: 'var(--text)' }}>
            {single
              ? t('orders.sapSyncDialogIntroSingle')
              : t('orders.sapSyncDialogIntroMany')}
          </p>

          {/* Blue informational callout — what happens after clicking confirm */}
          <Callout color="#3b82f6" icon={<FiClock />} title={t('orders.sapSyncDialogQueueWindowTitle')}>
            <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--text)' }}>
              <strong>{t('orders.sapSyncDialogQueueWindowValue')}</strong>{' '}
              {t('orders.sapSyncDialogQueueWindowDetail')}
            </div>
          </Callout>

          {/* Amber warning — check the orders before confirming */}
          <Callout color="#f59e0b" icon={<FiAlertTriangle />} title={t('orders.sapSyncDialogVerifyTitle')}>
            <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--text)' }}>
              {t('orders.sapSyncDialogVerifyText')}
            </div>
          </Callout>

          {/* Order list */}
          <div>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: '8px',
            }}>
              <div style={{
                fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                {single
                  ? t('orders.sapSyncDialogListHeaderSingle')
                  : t('orders.sapSyncDialogListHeaderMany', { count: orders.length })}
              </div>
              {sharedCurrency && orders.length > 1 && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {t('orders.sapSyncDialogTotal')}:{' '}
                  <strong style={{ color: 'var(--text)' }}>
                    {formatMoney(totalValue, sharedCurrency)}
                  </strong>
                </div>
              )}
            </div>

            <div style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              backgroundColor: 'var(--bg-elevated)',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead style={{
                  position: 'sticky', top: 0,
                  backgroundColor: 'var(--bg-elevated)',
                  borderBottom: '1px solid var(--border)',
                  zIndex: 1,
                }}>
                  <tr>
                    <Th>{t('orders.poNumber')}</Th>
                    <Th>{t('orders.channel')}</Th>
                    <Th>{t('orders.marketplace')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th align="right">{t('orders.amount')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, idx) => {
                    const cc = getMarketplaceCountry(o.marketplace);
                    const isVendor = o.channel === 1;
                    return (
                      <tr key={o.id} style={{
                        borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
                      }}>
                        <Td>
                          <span style={{
                            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                            fontWeight: 700, fontSize: '12px',
                          }}>
                            {o.amazonPurchaseOrderNumber}
                          </span>
                        </Td>
                        <Td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                            color: isVendor ? '#7c3aed' : '#3b82f6',
                            backgroundColor: isVendor ? 'rgba(124, 58, 237, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                          }}>
                            {channelLabel(o.channel)}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <CountryFlag countryCode={cc} size={14} />
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                              {o.marketplace || '—'}
                            </span>
                          </span>
                        </Td>
                        <Td>
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {statusLabel ? statusLabel(o.status) : `Status ${o.status}`}
                          </span>
                        </Td>
                        <Td align="right">
                          {o.totalAmount > 0 ? (
                            <span style={{ fontWeight: 600, color: '#10b981', fontSize: '12px' }}>
                              {formatMoney(o.totalAmount, o.currency)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* --- Footer --------------------------------------------------- */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onConfirm}
            disabled={loading || orders.length === 0}
            style={{
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6',
              color: '#fff',
              fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              opacity: loading ? 0.85 : 1,
            }}
          >
            <FiCheckCircle />
            {loading
              ? t('orders.sapSyncDialogSubmitting')
              : (single
                  ? t('orders.sapSyncDialogConfirmSingle')
                  : t('orders.sapSyncDialogConfirmMany', { count: orders.length }))}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tiny table cells -- consistent padding/colors                      */
/* ------------------------------------------------------------------ */

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th style={{
    textAlign: align,
    padding: '8px 12px',
    fontSize: '11px', fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }}>
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <td style={{
    textAlign: align,
    padding: '8px 12px',
    verticalAlign: 'middle',
    color: 'var(--text)',
  }}>
    {children}
  </td>
);

interface CalloutProps {
  color: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Callout: React.FC<CalloutProps> = ({ color, icon, title, children }) => (
  <div style={{
    display: 'flex',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '10px',
    backgroundColor: `${color}14`,
    border: `1px solid ${color}40`,
  }}>
    <div style={{ color, fontSize: '18px', flexShrink: 0, marginTop: '2px', display: 'inline-flex' }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '12px', fontWeight: 700, color,
        marginBottom: '4px',
        textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        {title}
      </div>
      {children}
    </div>
  </div>
);

void FiInfo;

export default ConfirmSapSyncDialog;
